import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.core.tween
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.TextUnit
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.zIndex
import banko.composeapp.generated.resources.Res
import banko.composeapp.generated.resources.mute_reactions
import banko.composeapp.generated.resources.reaction_bank_description
import banko.composeapp.generated.resources.reaction_button
import banko.composeapp.generated.resources.reaction_cooldown
import banko.composeapp.generated.resources.reaction_fire_description
import banko.composeapp.generated.resources.reaction_picker_title
import banko.composeapp.generated.resources.reaction_roll_description
import banko.composeapp.generated.resources.reaction_shock_description
import banko.composeapp.generated.resources.reaction_watching_description
import banko.composeapp.generated.resources.reactions_muted
import banko.composeapp.generated.resources.unmute_reactions
import models.TableReactionKind
import org.jetbrains.compose.resources.stringResource

/**
 * Replaces the disabled Roll action while another device owns the turn. The picker stays tucked
 * behind one familiar action-size button, so the permanent game layout does not grow.
 */
@Composable
internal fun TableReactionActionButton(
    uiState: BankoGameScreenUiState,
    modifier: Modifier = Modifier,
    fontSize: TextUnit = 18.sp,
) {
    val canSend by uiState.canSendTableReactionFlow.collectAsState()
    val muted by uiState.tableReactionsMutedFlow.collectAsState()
    var expanded by remember { mutableStateOf(false) }

    Box(modifier = modifier) {
        GlossySecondaryButton(
            text = if (muted) stringResource(Res.string.reactions_muted)
            else stringResource(Res.string.reaction_button),
            onClick = { expanded = true },
            fontSize = fontSize,
            modifier = Modifier.fillMaxSize(),
        )

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.widthIn(min = 286.dp),
        ) {
            Column(
                horizontalAlignment = Alignment.CenterHorizontally,
                modifier = Modifier.padding(horizontal = 10.dp, vertical = 6.dp),
            ) {
                Text(
                    text = stringResource(Res.string.reaction_picker_title),
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(bottom = 8.dp),
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    TableReactionKind.entries.forEach { kind ->
                        val enabled = canSend && !muted
                        val description = kind.accessibilityDescription()
                        Surface(
                            shape = if (kind == TableReactionKind.BANK) {
                                RoundedCornerShape(percent = 50)
                            } else {
                                CircleShape
                            },
                            color = MaterialTheme.colorScheme.secondaryContainer,
                            contentColor = MaterialTheme.colorScheme.onSecondaryContainer,
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                            modifier = Modifier
                                .then(
                                    if (kind == TableReactionKind.BANK) Modifier.size(58.dp, 42.dp)
                                    else Modifier.size(42.dp)
                                )
                                .alpha(if (enabled) 1f else 0.45f)
                                .semantics { contentDescription = description }
                                .clickable(enabled = enabled) {
                                    uiState.onTableReaction(kind)
                                    expanded = false
                                },
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(
                                    text = kind.tableSymbol(),
                                    fontSize = if (kind == TableReactionKind.BANK) 11.sp else 22.sp,
                                    fontWeight = if (kind == TableReactionKind.BANK) {
                                        FontWeight.Black
                                    } else {
                                        FontWeight.Normal
                                    },
                                    textAlign = TextAlign.Center,
                                )
                            }
                        }
                    }
                }
                HorizontalDivider(modifier = Modifier.padding(top = 10.dp))
                TextButton(
                    onClick = {
                        uiState.onToggleTableReactionsMuted()
                        expanded = false
                    },
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    Text(
                        if (muted) stringResource(Res.string.unmute_reactions)
                        else stringResource(Res.string.mute_reactions)
                    )
                }
                if (!canSend && !muted) {
                    Text(
                        text = stringResource(Res.string.reaction_cooldown),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

/** Short-lived, non-interactive bursts layered over the dice rather than inserted into the layout. */
@Composable
internal fun TableReactionOverlay(
    uiState: BankoGameScreenUiState,
    modifier: Modifier = Modifier,
) {
    val bursts by uiState.visibleTableReactionsFlow.collectAsState()
    val muted by uiState.tableReactionsMutedFlow.collectAsState()
    AnimatedVisibility(
        visible = bursts.isNotEmpty() && !muted,
        enter = fadeIn(tween(120)) + scaleIn(initialScale = 0.8f, animationSpec = tween(160)),
        exit = fadeOut(tween(180)) + scaleOut(targetScale = 0.9f, animationSpec = tween(180)),
        modifier = modifier.zIndex(4f),
    ) {
        Row(
            horizontalArrangement = Arrangement.spacedBy(8.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            bursts.forEach { burst ->
                // A duplicate changes the sequence, replaying the quick pop while the count climbs.
                key(burst.animationSequence) {
                    Surface(
                        shape = RoundedCornerShape(percent = 50),
                        color = MaterialTheme.colorScheme.inverseSurface,
                        contentColor = MaterialTheme.colorScheme.inverseOnSurface,
                        border = BorderStroke(
                            1.dp,
                            MaterialTheme.colorScheme.primary.copy(alpha = 0.65f),
                        ),
                        modifier = Modifier.shadow(8.dp, RoundedCornerShape(percent = 50)),
                    ) {
                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(4.dp),
                            modifier = Modifier.padding(horizontal = 12.dp, vertical = 7.dp),
                        ) {
                            Text(
                                text = burst.kind.tableSymbol(),
                                fontSize = if (burst.kind == TableReactionKind.BANK) 12.sp else 22.sp,
                                fontWeight = if (burst.kind == TableReactionKind.BANK) {
                                    FontWeight.Black
                                } else {
                                    FontWeight.Normal
                                },
                            )
                            if (burst.count > 1) {
                                Text(
                                    text = "×${burst.count}",
                                    style = MaterialTheme.typography.labelLarge,
                                    fontWeight = FontWeight.Bold,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

internal fun TableReactionKind.tableSymbol(): String = when (this) {
    TableReactionKind.WATCHING -> "👀"
    TableReactionKind.BANK -> "BANK!"
    TableReactionKind.ROLL -> "🎲"
    TableReactionKind.SHOCK -> "😱"
    TableReactionKind.FIRE -> "🔥"
}

@Composable
private fun TableReactionKind.accessibilityDescription(): String = stringResource(
    when (this) {
        TableReactionKind.WATCHING -> Res.string.reaction_watching_description
        TableReactionKind.BANK -> Res.string.reaction_bank_description
        TableReactionKind.ROLL -> Res.string.reaction_roll_description
        TableReactionKind.SHOCK -> Res.string.reaction_shock_description
        TableReactionKind.FIRE -> Res.string.reaction_fire_description
    }
)
